import { Loader } from '@googlemaps/js-api-loader';

export type GeocodedLocation = {
  latitude: number;
  longitude: number;
};

const ensureGoogleMapsLoaded = async (mapApiKey?: string): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  // Reuse Maps JS when already loaded (e.g. by useLoadScript) to avoid double-include errors.
  if (window.google?.maps) {
    return;
  }

  if (!mapApiKey) {
    return;
  }

  const loader = new Loader({
    apiKey: mapApiKey,
    version: 'weekly',
    libraries: ['geocoding'],
  });
  await loader.importLibrary('maps');
};

/**
 * Geocodes a US ZIP, city, and/or state string to coordinates.
 * Unlike getStateFromZipcodeCoveo, this accepts non–postal-code results (city/state).
 */
export const geocodeLocation = async (
  address: string,
  mapApiKey?: string
): Promise<GeocodedLocation | null> => {
  const trimmed = address?.trim();
  if (!trimmed) {
    return null;
  }

  await ensureGoogleMapsLoaded(mapApiKey);

  if (typeof window === 'undefined' || !window.google?.maps?.Geocoder) {
    return null;
  }

  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { address: trimmed, componentRestrictions: { country: 'US' } },
      (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          const location = results[0]?.geometry?.location;
          const latitude = location?.lat();
          const longitude = location?.lng();

          if (typeof latitude === 'number' && typeof longitude === 'number') {
            resolve({ latitude, longitude });
            return;
          }
        }
        resolve(null);
      }
    );
  });
};
